/* Mock API.
 *
 * The backend does not exist yet — Phase 0.5 has not run and B1 (the ten OAuth
 * scopes) is unresolved. This layer returns realistic data shaped exactly like
 * the real contract so the front end runs, is demoable, and can be tested today.
 *
 * To switch to the real API: set VITE_API_BASE and client.ts stops importing this.
 *
 * Data below uses real folder IDs, owners and group addresses drawn from
 * demo/catalogue-full.json (the live 12-drive entity-led catalogue, regenerated
 * 22 Sep) — every "Open in Drive" / "Open folder" link in the mock points at the
 * actual Shared Drive folder, not a placeholder. Drive's own sharing is the
 * access gate, so a real link is safe to show even to someone who cannot open it.
 */

import type {
  Leaderboard,
  MyAccess, LiveAccess, CatalogueFolder, SearchResult, MigrationQueue, ReverseLookup, AccessPreflight,
  AccessRequestResult, Inbox, InboxItem, RequestOutcomeKind,
  OwnersResponse, RegisterProposal, RegisterMe,
  MyGroups, GroupMember, GroupChange,
  MoveCheck, MoveResult, ProjectOptions, ProjectNameCheck, ProjectRequest, ProjectResult, FolderFile, FolderFilesPage } from '../types'

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Simulated persona: a Kenya-based procurement officer. The done-condition user. */
export const MOCK_USER = 'j.doe@dlight.com'

export const mockAccess: MyAccess = {
  email: MOCK_USER,
  resolvedAt: new Date().toISOString(),
  correlationId: 'c-8f21a4d0',
  unknownCount: 0,
  lockedCount: 41,
  drives: [
    {
      driveName: '01 RMS Group',
      confidentiality: 'Internal',
      folders: [
        { folderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', name: '04 Procurement', state: 'visible', access: 'edit',
          webViewLink: 'https://drive.google.com/drive/folders/1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF',
          grantedBy: 'grp-group-procurement-edit@dlight.com', accountableOwner: 'RMS Administrator',
          whatBelongsHere: 'Purchase orders, supplier files, tenders, RFQs, procurement policy, vendor onboarding',
          path: [{ folderId: 'mock-group-ops', name: '06 Operations', openable: false }], parentFolderId: 'mock-group-ops', entityCode: 'GROUP' },
        /* Tree fixtures (21 Oct 2026): a subfolder with its own grant under a level you pass through. */
        { folderId: 'mock-group-purchasing', name: 'Purchasing', state: 'visible', access: 'edit',
          webViewLink: 'https://drive.google.com/drive/folders/mock-group-purchasing',
          grantedBy: 'grp-global-procurement-edit@dlight.com', accountableOwner: 'RMS Administrator', entityCode: 'GROUP',
          parentFolderId: 'mock-group-contracts',
          path: [{ folderId: 'mock-group-ops', name: '06 Operations', openable: false },
            { folderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', name: '04 Procurement', openable: true },
            { folderId: 'mock-group-contracts', name: 'Contracts & Agreements', openable: false }] },
        { folderId: 'mock-group-vendors', name: 'Vendor Management', state: 'visible', access: 'edit',
          webViewLink: 'https://drive.google.com/drive/folders/mock-group-vendors',
          grantedBy: 'grp-group-procurement-edit@dlight.com', accountableOwner: 'RMS Administrator', entityCode: 'GROUP',
          parentFolderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF',
          path: [{ folderId: 'mock-group-ops', name: '06 Operations', openable: false },
            { folderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', name: '04 Procurement', openable: true }] },
        { folderId: '1O9zVAfy5LFmUXNF8eHidE1LgQSGCMJZW', name: '05 Credit Risk Management', state: 'visible', access: 'view',
          webViewLink: 'https://drive.google.com/drive/folders/1O9zVAfy5LFmUXNF8eHidE1LgQSGCMJZW',
          grantedBy: 'grp-group-creditriskman-view@dlight.com', accountableOwner: 'Paul Kebati' },
        { folderId: '1xjLrzSzqrXcLF4Xo2jbsjt0e6gnhZngP', name: '01 Controllership & Statutory Reporting', state: 'not-visible', access: 'none',
          accountableOwner: 'Paul Kebati' },
      ],
    },
    {
      driveName: '02 RMS KE Kenya',
      confidentiality: 'Internal',
      folders: [
        { folderId: '112aHlh8nyWEQEJI6namJ4mN6NEkPrwgv', name: '04 Procurement', state: 'visible', access: 'edit',
          webViewLink: 'https://drive.google.com/drive/folders/112aHlh8nyWEQEJI6namJ4mN6NEkPrwgv',
          grantedBy: 'grp-ke-procurement-edit@dlight.com', accountableOwner: 'RMS Administrator',
          whatBelongsHere: 'Purchase orders, supplier files, tenders, RFQs, procurement policy, vendor onboarding — Kenya',
          path: [{ folderId: 'mock-ke-ops', name: '06 Operations', openable: false }], parentFolderId: 'mock-ke-ops', entityCode: 'KE' },
      ],
    },
    {
      /* The cross-grant case. Not a member of the Legal drive, but holds a
         folder-level view grant. This is why the portal probes folder IDs
         instead of calling drives.list. */
      driveName: '01 RMS Group',
      confidentiality: 'Confidential',
      folders: [
        { folderId: '1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp', name: '01 Contracts & Agreements', state: 'visible', access: 'view',
          webViewLink: 'https://drive.google.com/drive/folders/1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp',
          grantedBy: 'grp-group-contractsagre-view@dlight.com', accountableOwner: 'Gathoni Mukira' },
        { folderId: '1ugmG1nRJCCvrqTbpRxWmuZNrKk1_-F0n', name: '03 Disputes & Litigation', state: 'not-visible', access: 'none',
          accountableOwner: 'Gathoni Mukira' },
        { folderId: '1s7jw4q5ttdvVak4y0BtBXAvpQ_9BBkJ8', name: '04 Data Protection', state: 'not-visible', access: 'none',
          accountableOwner: 'Gathoni Mukira' },
      ],
    },
    {
      driveName: '00 RMS Company Hub',
      confidentiality: 'Internal',
      folders: [
        { folderId: '18m3V0ix9aIJFVlWpJj-vv1qpUTH56znS', name: '01 Policies & Playbooks', state: 'visible', access: 'view',
          webViewLink: 'https://drive.google.com/drive/folders/18m3V0ix9aIJFVlWpJj-vv1qpUTH56znS',
          grantedBy: 'grp-hub-core@dlight.com', accountableOwner: 'RMS Administrator' },
        { folderId: '1I5o-lvnVoTr_OqDhGW3yHlz5Fi5BTYMQ', name: '04 Onboarding & Training', state: 'visible', access: 'view',
          webViewLink: 'https://drive.google.com/drive/folders/1I5o-lvnVoTr_OqDhGW3yHlz5Fi5BTYMQ',
          grantedBy: 'grp-hub-core@dlight.com', accountableOwner: 'RMS Administrator' },
      ],
    },
  ],
}

export const mockCatalogue: CatalogueFolder[] = [
  { folderId: '1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp', driveName: '01 RMS Group', driveOrder: 1, confidentiality: 'Confidential',
    name: '01 Contracts & Agreements', accountableOwner: 'Gathoni Mukira', ownerEmail: null,
    whatBelongsHere: 'Executed contracts, vendor agreements, NDAs, commercial terms, service agreements, call centre and supplier contracts',
    grantingGroups: ['grp-group-contractsagre-edit@dlight.com'], isEntityBranch: false, entityCode: null,
    webViewLink: 'https://drive.google.com/drive/folders/1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp' },
  { folderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', driveName: '01 RMS Group', driveOrder: 1, confidentiality: 'Internal',
    name: '04 Procurement', accountableOwner: 'RMS Administrator', ownerEmail: null,
    whatBelongsHere: 'Purchase orders, supplier files, tenders, RFQs, procurement policy, vendor onboarding',
    grantingGroups: ['grp-group-procurement-edit@dlight.com'], isEntityBranch: false, entityCode: null,
    webViewLink: 'https://drive.google.com/drive/folders/1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF' },
  /* Entity-branch fixture (R0) — a real tier-2 country subfolder, so score()'s
     entityCode filter/boost and File.tsx's stamped-Entity display have
     something true to verify against, not just non-branch folders. */
  { folderId: '112aHlh8nyWEQEJI6namJ4mN6NEkPrwgv', driveName: '02 RMS KE Kenya', driveOrder: 2, confidentiality: 'Internal',
    name: '04 Procurement', accountableOwner: 'RMS Administrator', ownerEmail: null,
    whatBelongsHere: 'Purchase orders, supplier files, tenders, RFQs, procurement policy, vendor onboarding — Kenya',
    grantingGroups: ['grp-ke-procurement-edit@dlight.com'], isEntityBranch: true, entityCode: 'KE',
    webViewLink: 'https://drive.google.com/drive/folders/112aHlh8nyWEQEJI6namJ4mN6NEkPrwgv' },
  { folderId: '1O9zVAfy5LFmUXNF8eHidE1LgQSGCMJZW', driveName: '01 RMS Group', driveOrder: 1, confidentiality: 'Internal',
    name: '05 Credit Risk Management', accountableOwner: 'Paul Kebati', ownerEmail: null,
    whatBelongsHere: 'Credit policy, scoring models, collections, portfolio reports, write-off approvals',
    grantingGroups: ['grp-group-creditriskman-edit@dlight.com'], isEntityBranch: false, entityCode: null,
    webViewLink: 'https://drive.google.com/drive/folders/1O9zVAfy5LFmUXNF8eHidE1LgQSGCMJZW' },
  { folderId: '1xjLrzSzqrXcLF4Xo2jbsjt0e6gnhZngP', driveName: '01 RMS Group', driveOrder: 1, confidentiality: 'Confidential',
    name: '01 Controllership & Statutory Reporting', accountableOwner: 'Paul Kebati', ownerEmail: null,
    whatBelongsHere: 'Statutory accounts, audit files, trial balance, month-end close, statutory filings',
    grantingGroups: ['grp-group-controllership-edit@dlight.com'], isEntityBranch: false, entityCode: null,
    webViewLink: 'https://drive.google.com/drive/folders/1xjLrzSzqrXcLF4Xo2jbsjt0e6gnhZngP' },
  { folderId: '1dJrJV1Tb4ZRIAKpDbupW2pxaQUKJ-p_F', driveName: '01 RMS Group', driveOrder: 1, confidentiality: 'Restricted',
    name: '02 Board & Shareholder', accountableOwner: 'RMS Administrator', ownerEmail: null,
    whatBelongsHere: 'Board minutes, board packs, shareholder resolutions, AGM papers',
    grantingGroups: ['grp-group-boardshareho-edit@dlight.com'], isEntityBranch: false, entityCode: null,
    webViewLink: 'https://drive.google.com/drive/folders/1dJrJV1Tb4ZRIAKpDbupW2pxaQUKJ-p_F' },
  { folderId: '15tUVQhBIyFfWLEP4N53dLOzki4dwnGcf', driveName: '01 RMS Group', driveOrder: 1, confidentiality: 'Internal',
    name: '01 Learning & Development', accountableOwner: 'Elson Mushi', ownerEmail: null,
    whatBelongsHere: 'Training material, course content, learning paths, facilitator guides',
    grantingGroups: ['grp-group-learningdeve-edit@dlight.com'], isEntityBranch: false, entityCode: null,
    webViewLink: 'https://drive.google.com/drive/folders/15tUVQhBIyFfWLEP4N53dLOzki4dwnGcf' },
]

export const mockSearch: SearchResult[] = [
  { id: 's1', name: 'KE_LEG_CallCentreVendorContract_20260817', mimeType: 'application/pdf',
    webViewLink: '#', driveName: '03 Legal & Compliance', modifiedTime: '2026-08-17',
    labels: { 'Record Series': 'Contract', Entity: 'KE_Kenya', 'Lifecycle Status': 'Approved' } },
  { id: 's2', name: 'KE_LEG_CallCentreSLA_20260612', mimeType: 'application/vnd.google-apps.document',
    webViewLink: '#', driveName: '03 Legal & Compliance', modifiedTime: '2026-06-12',
    labels: { 'Record Series': 'Contract', Entity: 'KE_Kenya', 'Lifecycle Status': 'Approved' } },
]

export const mockMigration: MigrationQueue = {
  pendingCount: 47,
  confidentCount: 39,
  needsHumanCount: 8,
  consentGiven: false,
  hasScanned: true,
  items: [
    { itemId: 'm1', fileName: 'Procurement tracker 2026.xlsx', mimeType: 'sheet',
      currentLocation: 'myDrive', legacyDriveName: null,
      suggestedFolderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', suggestedFolderName: '01 RMS Group / 04 Procurement',
      confidence: 0.94, reason: 'Legacy code OP-6-08 → Procurement', classifier: 'rule',
      externallyShared: false, status: 'pending' },
    { itemId: 'm2', fileName: 'Vendor onboarding checklist.docx', mimeType: 'document',
      currentLocation: 'legacySharedDrive', legacyDriveName: 'OPCO_Procurement',
      suggestedFolderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', suggestedFolderName: '01 RMS Group / 04 Procurement',
      confidence: 0.88, reason: 'Legacy drive name matches tier-1 folder', classifier: 'rule',
      externallyShared: false, status: 'pending' },
    { itemId: 'm3', fileName: 'Notes from Nairobi trip.docx', mimeType: 'document',
      currentLocation: 'myDrive', legacyDriveName: null,
      suggestedFolderId: null, suggestedFolderName: null,
      confidence: 0.31, reason: 'No rule matched — needs a human', classifier: 'none',
      externallyShared: false, status: 'pending' },
    { itemId: 'm4', fileName: 'Supplier pricing (shared with vendor).xlsx', mimeType: 'sheet',
      currentLocation: 'myDrive', legacyDriveName: null,
      suggestedFolderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', suggestedFolderName: '01 RMS Group / 04 Procurement',
      confidence: 0.91, reason: 'Legacy code OP-6-08 → Procurement', classifier: 'rule',
      externallyShared: true, status: 'quarantined' },
  ],
}

export const mockReverse: Record<string, ReverseLookup> = {
  '1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp': {
    folderId: '1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp', folderName: '01 Contracts & Agreements',
    driveName: '01 RMS Group', accountableOwner: 'Gathoni Mukira',
    groups: [
      { email: 'grp-group-contractsagre-edit@dlight.com', memberCount: 6, role: 'writer' },
      { email: 'grp-group-contractsagre-view@dlight.com', memberCount: 28, role: 'reader' },
    ],
  },
}

/* R1 (adoption addendum) — mock for GET /api/me/access/preflight/:folderId.
   Deliberately covers all four wizard states (file / read / locked /
   unknown) across the filing catalogue above, not just the folders that
   already appear in mockAccess, so the access-aware resolution and its
   alternates row are all exercisable against `npm run dev` without a
   backend. A folder absent from this table (see getPreflight below) falls
   back to 'unknown' + no write access — never a silent "edit". */
const mockPreflight: Record<string, AccessPreflight> = {
  '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF': { folderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', state: 'visible', canAddChildren: true },
  '112aHlh8nyWEQEJI6namJ4mN6NEkPrwgv': { folderId: '112aHlh8nyWEQEJI6namJ4mN6NEkPrwgv', state: 'visible', canAddChildren: true },
  '1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp': { folderId: '1L89aRLmOWpztzb_SoveYkGb3aTy2QDWp', state: 'visible', canAddChildren: false },
  '1O9zVAfy5LFmUXNF8eHidE1LgQSGCMJZW': { folderId: '1O9zVAfy5LFmUXNF8eHidE1LgQSGCMJZW', state: 'visible', canAddChildren: false },
  '1xjLrzSzqrXcLF4Xo2jbsjt0e6gnhZngP': { folderId: '1xjLrzSzqrXcLF4Xo2jbsjt0e6gnhZngP', state: 'not-visible', canAddChildren: false },
  '1dJrJV1Tb4ZRIAKpDbupW2pxaQUKJ-p_F': { folderId: '1dJrJV1Tb4ZRIAKpDbupW2pxaQUKJ-p_F', state: 'not-visible', canAddChildren: false },
  '15tUVQhBIyFfWLEP4N53dLOzki4dwnGcf': { folderId: '15tUVQhBIyFfWLEP4N53dLOzki4dwnGcf', state: 'unknown', canAddChildren: false },
}

/* Deliberately slow enough that loading states are visible in development.
   Drop MOCK_LATENCY to 0 for tests. */
const MOCK_LATENCY = 550

/* Migration leaderboard: staging figures, 22 Sep – 25 Oct 2026. */
const MOCK_BOARD: [string, string, number, number, number][] = [
  ['Finance', 'Ronald Pfende', 2332, 2331, 2], ['Supply Chain', 'Tony Xiu', 1148, 0, 5],
  ['Sales', 'Country MDs and Country Sales Leadership', 807, 0, 1], ['People & Culture', 'Elson Mushi', 316, 0, 2],
  ['Legal & Compliance', 'Gathoni Mukira', 221, 0, 2], ['Technology', 'Eugenio Rodriguez', 217, 0, 3],
  ['Procurement', 'Winfridah Mwongera', 194, 192, 2], ['Sales Operations', 'Aghiles Ourad', 173, 172, 2],
  ['Credit Risk', 'Donal Connolly', 109, 109, 1], ['Quality', 'Vivek Gupta', 17, 0, 3],
  ['After Sales', 'Stephen Maingi', 2, 0, 1], ['Business Intelligence', 'Jack Pegler', 2, 0, 1],
  ['Customer Experience', 'Patricia Lukong', 2, 2, 1], ['Executives', 'Nedjip Tozun', 0, 0, 0],
  ['Global Partnerships', 'Shailesh Gupta', 0, 0, 0], ['Marketing', 'Anshu Katiyar', 0, 0, 0],
  ['Product Design & Engineering', 'Karl Skare', 0, 0, 0], ['Strategy', 'Hendrik Hoelzer', 0, 0, 0],
]

const MOCK_SPONSORS = [
  { division: '01 Corporate & Governance', name: 'Shailesh Gupta' }, { division: '02 Legal', name: 'Gathoni Mukira' },
  { division: '03 Finance & Accounts', name: 'Ronald Pfende' }, { division: '04 People (HR)', name: 'Elson Mushi' },
  { division: '05 Technology', name: 'Eugenio Rodriguez' }, { division: '06 Operations', name: 'Jacob Lewandowski' },
  { division: '07 Strategy & Product', name: 'Karl Skare' },
]

export const mockApi = {
  /* Start a project. "2026 RMS" is taken. */
  async getProjectsEnabled(): Promise<{ enabled: boolean }> { return { enabled: true } },
  async getProjectOptions(): Promise<ProjectOptions> {
    return { sponsors: MOCK_SPONSORS, subfolders: ['01 Governance', '02 Plan & Status', '03 Working', '04 Deliverables', '05 Closure'] }
  },
  async checkProjectName(name: string): Promise<ProjectNameCheck> {
    const n = name.trim().replace(/\s+/g, ' ')
    if (!/^20\d\d .{2,60}$/.test(n)) return { ok: false, message: 'Name the project as the year and a short name, for example "2026 Pricing Review".' }
    if (/ rms$/i.test(n)) return { ok: false, name: n, message: 'A project with this name already exists in 09 Projects. Pick a different name, or ask its lead to add you.' }
    const slug = n.slice(5).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    return { ok: true, name: n, group: `grp-project-${slug}-edit@dlight.com` }
  },
  async startProject(req: ProjectRequest): Promise<ProjectResult> {
    const c = await this.checkProjectName(req.name)
    if (!c.ok) throw Object.assign(new Error(c.message), { correlationId: 'c-mock' })
    const sponsor = MOCK_SPONSORS.find((s) => s.division === req.sponsor) ?? MOCK_SPONSORS[0]
    return {
      ok: true, name: c.name!, group: c.group!, folderId: 'mockProject000', webViewLink: 'https://drive.google.com/drive/folders/mockProject000',
      confidential: req.confidential, memberFailures: [], sponsor, sponsorNotified: true,
    }
  },

  /* Move folder for me. A link containing "mydrive" or "hidden" shows those refusals. */
  async getMoveEnabled(): Promise<{ enabled: boolean }> { return { enabled: true } },

  /* Folder files: 60 made-up files so Show more has a second page. */
  async getFolderFilesEnabled(): Promise<{ enabled: boolean }> { return { enabled: true } },
  async getFolderFiles(folderId: string, pageToken?: string): Promise<FolderFilesPage> {
    await delay(MOCK_LATENCY)
    const kinds = ['application/vnd.google-apps.document', 'application/vnd.google-apps.spreadsheet', 'application/pdf', 'image/png']
    const all: FolderFile[] = Array.from({ length: 60 }, (_, i) => ({
      id: `${folderId}-f${i}`, name: `Mock file ${i + 1}`, mimeType: kinds[i % kinds.length], isShortcut: i % 9 === 4,
      webViewLink: 'https://drive.google.com/', modifiedTime: new Date(Date.now() - i * 86_400_000).toISOString(),
      modifiedBy: i % 5 === 3 ? null : 'Mock Person',
    }))
    const start = pageToken ? Number(pageToken) : 0
    return { files: all.slice(start, start + 50), nextPageToken: start + 50 < all.length ? String(start + 50) : null }
  },
  async checkMove(source: string, destinationFolderId?: string): Promise<MoveCheck> {
    await delay(MOCK_LATENCY)
    const src = { folderId: 'mockSourceFolder01', name: '9. ZM Incident Reports', driveName: null }
    const destFolder = mockAccess.drives.flatMap((d) => d.folders.map((f) => ({ f, d }))).find((x) => x.f.folderId === destinationFolderId)
    const destination = destFolder ? { folderId: destFolder.f.folderId, name: destFolder.f.name, driveName: destFolder.d.driveName } : null
    if (/mydrive/i.test(source)) {
      return { ok: false, source: src, destination, files: 0, folders: 0, blockers: [{ code: 'my_drive', message: 'This folder is in your My Drive. Google only moves a whole folder out of My Drive inside Drive itself, so ask in rms-help and we will move it with you.' }] }
    }
    if (/hidden/i.test(source)) {
      return { ok: false, source: src, destination, files: 14, folders: 2, blockers: [{ code: 'hidden_items', message: '3 items inside this folder are hidden from you. You can only move a folder when you can open everything in it. Ask its owner, or rms-help.' }] }
    }
    return { ok: !!destination, source: src, destination, files: 14, folders: 2, blockers: [] }
  },
  async moveFolder(source: string, destinationFolderId: string): Promise<MoveResult> {
    const c = await this.checkMove(source, destinationFolderId)
    if (!c.ok || !c.source || !c.destination) throw new Error(c.blockers[0]?.message ?? 'Pick a destination.')
    return { ok: true, moveId: 'mock-move', source: c.source, destination: c.destination, files: c.files, folders: c.folders, webViewLink: `https://drive.google.com/drive/folders/${c.source.folderId}` }
  },
  async getLeaderboard(): Promise<Leaderboard> {
    await delay(MOCK_LATENCY)
    const rows = MOCK_BOARD.map(([department, head, files, lastWeek, movers], i) => ({
      rank: MOCK_BOARD.findIndex((x) => x[2] === files) + 1 || i + 1, department, head, files, lastWeek, movers, staff: 0,
    }))
    return { deadline: '2026-10-30', daysLeft: 5, observedFrom: '2026-09-22', observedThrough: '2026-10-25', missingDates: [], asOf: null, totalFiles: 5547, rows, notOnRoster: 7, noNamedPerson: 0 }
  },
  async whoami(): Promise<{ email: string } | null> { await delay(80); return { email: 'mock.user@dlight.com' } },
  /* Mirrors the live API (24 Sep 2026): locked folders are counted, not listed. */
  async getAccess(): Promise<MyAccess> {
    await delay(MOCK_LATENCY)
    const drives = mockAccess.drives
      .map((d) => ({ ...d, folders: d.folders.filter((f) => f.state !== 'not-visible') }))
      .filter((d) => d.folders.length > 0)
    return { ...mockAccess, drives }
  },
  /* Epic v4 A2: one folder made in Drive a minute ago, not yet registered. */
  async getAccessLive(): Promise<LiveAccess | null> {
    await delay(1500)
    return { status: 'ok', drives: [{ driveName: '01 RMS Group', confidentiality: 'Internal', folders: [{
      folderId: 'mock-live-tenders-2026', name: 'Tenders 2026', state: 'visible', access: 'edit', provisional: true,
      webViewLink: 'https://drive.google.com/drive/folders/mock-live-tenders-2026', accountableOwner: '', whatBelongsHere: '',
      entityCode: 'GROUP', parentFolderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF',
      path: [{ folderId: 'mock-group-ops', name: '06 Operations', openable: false },
        { folderId: '1qx45kJn-eh2yTtwsicUrRAPjAqEH9HZF', name: '04 Procurement', openable: true }],
    }] }] }
  },
  async getCatalogue(): Promise<CatalogueFolder[]> { await delay(120); return mockCatalogue },
  async search(q: string): Promise<SearchResult[]> {
    await delay(MOCK_LATENCY)
    if (!q.trim()) return []
    const needle = q.toLowerCase()
    return mockSearch.filter((r) => r.name.toLowerCase().includes(needle))
  },
  async getMigration(): Promise<MigrationQueue> { await delay(MOCK_LATENCY); return mockMigration },
  async getReverse(folderId: string): Promise<ReverseLookup | null> {
    await delay(300); return mockReverse[folderId] ?? null
  },
  async requestAccess(folderId: string, _reason: string): Promise<AccessRequestResult> {
    await delay(400)
    /* Mirrors prod today: owner notification is a stub, so ok is false and the
       requester gets a contact to email themselves. */
    return {
      ok: false, delivered: [], requestId: 'mock-req-1', queued: false,
      contact: { name: 'Head of Procurement', email: 'procurement-owner@dlight.com', interim: false },
      folderLink: `https://drive.google.com/drive/folders/${folderId}`,
      folderName: 'Procurement', driveName: 'Mock drive',
      message: 'Your request is recorded, but Head of Procurement has not been told automatically. Email them using the button below.',
    }
  },
  async getOwners(): Promise<OwnersResponse> {
    const cat = await this.getCatalogue()
    /* Mock caller is Group staff: the Company Hub and Group drives are their area, Kenya is not. */
    const folders = cat.map((f, i) => ({
      folderId: f.folderId, driveName: f.driveName, driveOrder: f.driveOrder, confidentiality: f.confidentiality,
      name: f.name, whatBelongsHere: f.whatBelongsHere, isEntityBranch: f.isEntityBranch, entityCode: f.entityCode,
      webViewLink: f.webViewLink, deputy: null, inMyArea: f.driveOrder <= 1, canOpen: null,
      ...(mockRegister[f.folderId]
        ? { ownerStatus: 'approved' as const, accountableOwner: mockRegister[f.folderId].name, ownerEmail: mockRegister[f.folderId].email }
        : i % 5 === 4
          ? { ownerStatus: 'missing' as const, accountableOwner: 'Owner assignment required', ownerEmail: null }
          : { ownerStatus: 'provisional' as const, accountableOwner: f.accountableOwner, ownerEmail: f.ownerEmail }),
    }))
    return { folders, myArea: 'mine' }
  },
  async getRegisterMe(): Promise<RegisterMe> {
    return { isApprover: true, approversConfigured: 1, waitingForMe: mockProposals.filter((p) => p.status === 'proposed').length, mine: 0 }
  },
  async getProposals(): Promise<{ isApprover: boolean; items: RegisterProposal[] }> {
    await delay(300)
    return { isApprover: true, items: mockProposals.map((p) => ({ ...p, canDecide: p.status === 'proposed' })) }
  },
  async proposeOwner(folderId: string, ownerEmail: string, deputyEmail: string, reason: string): Promise<{ proposal: RegisterProposal }> {
    await delay(300)
    const p: RegisterProposal = {
      proposalId: `mock-p-${mockProposals.length + 1}`, folderId, folderName: folderId, driveName: 'Mock drive',
      before: { owner: null, deputy: null, status: 'missing' },
      after: { owner: { email: ownerEmail, name: ownerEmail.split('@')[0] }, deputy: deputyEmail ? { email: deputyEmail, name: deputyEmail.split('@')[0] } : null },
      reason, proposedBy: 'mock.user@dlight.com', proposedAt: new Date().toISOString(), status: 'proposed', decidedBy: null, decidedAt: null, decisionNote: null,
    }
    mockProposals.unshift(p)
    return { proposal: p }
  },
  async decideProposal(proposalId: string, decision: 'approve' | 'reject', note: string): Promise<{ proposal: RegisterProposal }> {
    await delay(300)
    const p = mockProposals.find((x) => x.proposalId === proposalId)!
    Object.assign(p, { status: decision === 'approve' ? 'approved' : 'rejected', decidedBy: 'mock.user@dlight.com', decidedAt: new Date().toISOString(), decisionNote: note || null })
    if (decision === 'approve') mockRegister[p.folderId] = p.after.owner
    return { proposal: p }
  },
  async getMyGroups(): Promise<MyGroups> {
    await delay(250)
    return {
      changesEnabled: true,
      groups: [{
        email: 'grp-ke-sales-operations-edit@dlight.com',
        owners: ['mock.user@dlight.com'],
        checkedAt: new Date().toISOString(),
        folders: [
          { folderId: 'm1', name: '08 Sales Operations · KE', driveName: 'KE', confidentiality: 'Internal', limitedAccess: false },
          { folderId: 'm2', name: 'Incentives & Commissions', driveName: 'KE', confidentiality: 'Confidential', limitedAccess: true },
        ],
      }],
    }
  },
  async getGroupMembers(_group: string): Promise<{ members: GroupMember[] }> {
    await delay(300)
    return { members: [...mockMembers] }
  },
  async changeGroupMember(group: string, action: 'add' | 'remove', email: string, reason: string, role: 'MEMBER' | 'OWNER' = 'MEMBER'): Promise<{ change: GroupChange }> {
    await delay(400)
    const prior = mockMembers.find((m) => m.email === email)
    const had = !!prior
    const already = action === 'add' ? had && (role === 'MEMBER' || prior!.role === 'OWNER') : !had
    if (action === 'add' && !had) mockMembers.push({ email, role, type: 'USER' })
    if (action === 'add' && had && role === 'OWNER') mockMembers = mockMembers.map((m) => (m.email === email ? { ...m, role: 'OWNER' } : m))
    if (action === 'remove') mockMembers = mockMembers.filter((m) => m.email !== email)
    const at = new Date().toISOString()
    const change: GroupChange = { changeId: crypto.randomUUID(), group, action, role, memberEmail: email, reason, actorEmail: 'mock.user@dlight.com',
      status: already ? 'already' : 'confirmed', error: null, createdAt: at, finishedAt: at }
    mockChanges.unshift(change)
    return { change }
  },
  async getGroupChanges(_group: string): Promise<{ changes: GroupChange[] }> {
    await delay(200)
    return { changes: mockChanges }
  },
  async getInbox(): Promise<Inbox> {
    await delay(300)
    return { items: mockInbox, open: mockInbox.filter((i) => i.status === 'open').length }
  },
  async resolveRequest(requestId: string, outcome: RequestOutcomeKind, note: string): Promise<{ item: InboxItem }> {
    await delay(300)
    const item = mockInbox.find((i) => i.requestId === requestId)!
    Object.assign(item, { status: 'closed', outcome, outcomeNote: note || null, resolvedAt: new Date().toISOString(), resolvedBy: 'you@dlight.com' })
    return { item }
  },
  async file(_folderId: string, _idempotencyKey: string): Promise<{ ok: boolean; fileId: string }> {
    await delay(500)
    return { ok: true, fileId: 'mock-file-id' }
  },
  async getPreflight(folderId: string): Promise<AccessPreflight> {
    await delay(260)
    return mockPreflight[folderId] ?? { folderId, state: 'unknown', canAddChildren: false }
  },
  async move(itemIds: string[]): Promise<{ moved: string[]; failed: string[] }> {
    await delay(500)
    const moved: string[] = []
    for (const id of itemIds) {
      const item = mockMigration.items.find((i) => i.itemId === id)
      if (item && item.status === 'pending') { item.status = 'moved'; moved.push(id) }
    }
    mockMigration.items = mockMigration.items.filter((i) => i.status === 'pending' || i.status === 'quarantined')
    mockMigration.pendingCount = mockMigration.items.filter((i) => i.status === 'pending').length
    mockMigration.confidentCount = mockMigration.items.filter((i) => i.status === 'pending' && i.suggestedFolderId).length
    return { moved, failed: itemIds.filter((id) => !moved.includes(id)) }
  },
}

let mockMembers: GroupMember[] = [
  { email: 'mock.user@dlight.com', role: 'OWNER', type: 'USER' },
  { email: 'frank.ojure@dlight.com', role: 'MEMBER', type: 'USER' },
  { email: 'gillian.gichohi@dlight.com', role: 'MEMBER', type: 'USER' },
]
const mockChanges: GroupChange[] = []

const mockInbox: InboxItem[] = [
  {
    requestId: 'mock-req-1', requesterEmail: 'new.hire@dlight.com', folderId: 'f-proc', folderName: '04 Procurement',
    driveName: '02 RMS KE Kenya', reason: 'Requested from Who owns what', status: 'open',
    createdAt: '2026-09-30T07:10:00.000Z', routedAsInterim: false, outcome: null, outcomeNote: null, resolvedAt: null, resolvedBy: null,
  },
  {
    requestId: 'mock-req-0', requesterEmail: 'analyst@dlight.com', folderId: 'f-bi', folderName: '01 Business Intelligence',
    driveName: '01 RMS Group', reason: 'Requested from My access', status: 'closed',
    createdAt: '2026-09-28T09:00:00.000Z', routedAsInterim: false, outcome: 'added-in-google', outcomeNote: null,
    resolvedAt: '2026-09-28T11:00:00.000Z', resolvedBy: 'you@dlight.com',
  },
]

const mockRegister: Record<string, { email: string; name: string }> = {}
const mockProposals: RegisterProposal[] = [
  {
    proposalId: 'mock-p-0', folderId: 'f-recruit', folderName: '02 Recruitment', driveName: '02 RMS KE Kenya',
    before: { owner: null, deputy: null, status: 'missing' },
    after: { owner: { email: 'jane.hr@dlight.com', name: 'Jane Hr' }, deputy: null },
    reason: 'Runs recruitment for Kenya', proposedBy: 'hr.head@dlight.com', proposedAt: '2026-09-30T08:00:00.000Z',
    status: 'proposed', decidedBy: null, decidedAt: null, decisionNote: null,
  },
]
